import { z } from "zod";

// ---------------------------------------------------------------------------
// High fives (GRA-256): the sketch's "cheer"/"goodEx" views. A leader
// recognises a team member with a short note, optionally tied to a goal, a
// routine or a KPI. Stored at:
//   Businesses/{businessId}/highFives/{highFiveId}
//
// A team's high fives ARE its "good examples" (goodEx): the same records,
// newest first, shown on Idag and on the team page. The employee's own feed
// (their high fives, read-only) is parked until the employee app exists
// (GRA-133) - the model already supports it via `toMemberId`.
//
// Anser can suggest a high five from a result (a target reached, a KPI
// moving), but a person always sends it: `source` only marks where the text
// started, `createdBy` is always the sender's uid.
// ---------------------------------------------------------------------------

export const HighFiveSource = z.enum(["manual", "anser"]);
export type HighFiveSource = z.infer<typeof HighFiveSource>;

export interface HighFiveRecord {
  id: string;
  businessId: string;
  salesTeamId: string;
  toMemberId: string;
  fromMemberId: string;
  message: string;
  linkedGoalId: string | null;
  linkedRoutineId: string | null;
  linkedKpiDefinitionId: string | null;
  source: HighFiveSource;
  createdBy: string | null;
  createdAt: string | null;
}

export const ListHighFivesInput = z.object({
  businessId: z.string().min(1),
  /** The team's good-examples feed. */
  salesTeamId: z.string().min(1).optional(),
  /** One member's own high fives. */
  toMemberId: z.string().min(1).optional(),
  limit: z.number().int().min(1).max(100).optional(),
});
export type ListHighFivesInput = z.infer<typeof ListHighFivesInput>;

export const CreateHighFiveInput = z.object({
  businessId: z.string().min(1),
  salesTeamId: z.string().min(1),
  toMemberId: z.string().min(1),
  message: z.string().trim().min(1).max(500),
  linkedGoalId: z.string().min(1).optional(),
  linkedRoutineId: z.string().min(1).optional(),
  linkedKpiDefinitionId: z.string().min(1).optional(),
  /** Defaults to "manual"; "anser" when the text started from a suggestion. */
  source: HighFiveSource.optional(),
});
export type CreateHighFiveInput = z.infer<typeof CreateHighFiveInput>;
